import { cn } from "@/lib/utils";
import { formatCurrency, formatDateShort } from "@/lib/format";
import { StatusPill } from "@/components/therapist/settings/section-primitives";

const STAT_TONE: Record<"primary" | "pending" | "paid", { dot: string; value: string }> = {
  primary: { dot: "bg-primary", value: "text-foreground" },
  pending: { dot: "bg-amber-500", value: "text-amber-700" },
  paid: { dot: "bg-emerald-500", value: "text-emerald-700" },
};

export function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "primary" | "pending" | "paid";
}) {
  const s = STAT_TONE[tone];
  return (
    <div className="rounded-2xl border border-border/60 bg-card px-5 py-4 shadow-sm">
      <div className="flex items-center gap-2">
        <span className={cn("size-1.5 rounded-full", s.dot)} />
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
      </div>
      <p
        className={cn("mt-2 text-3xl font-semibold leading-none tabular-nums", s.value)}
        style={{ fontFamily: "var(--font-heading)" }}
      >
        {value}
      </p>
    </div>
  );
}

export interface PayoutSessionRow {
  id: string;
  scheduledAt: string;
  clientName: string;
  serviceName: string | null;
  therapistEarnings: number | null;
  paymentStatus: string;
  payoutId: string | null;
}

export function SessionPayoutList({
  sessions,
  isFetching,
  emptyLabel,
  statusLabels,
}: {
  sessions: PayoutSessionRow[];
  isFetching: boolean;
  emptyLabel: string;
  statusLabels: { paid: string; pending: string; unpaid: string };
}) {
  if (sessions.length === 0 && !isFetching) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <div>
      {sessions.map((s) => {
        const paidOut = !!s.payoutId;
        const awaitingPayout = !paidOut && s.paymentStatus === "paid";
        return (
          <SessionPayoutRow
            key={s.id}
            date={formatDateShort(s.scheduledAt)}
            client={s.clientName}
            service={s.serviceName ?? "·"}
            earned={s.therapistEarnings ?? 0}
            tone={paidOut ? "ready" : awaitingPayout ? "warning" : "neutral"}
            statusLabel={paidOut ? statusLabels.paid : awaitingPayout ? statusLabels.pending : statusLabels.unpaid}
          />
        );
      })}
    </div>
  );
}

export function SessionPayoutRow({
  date,
  client,
  service,
  earned,
  tone,
  statusLabel,
}: {
  date: string;
  client: string;
  service: string;
  earned: number;
  tone: "ready" | "warning" | "neutral";
  statusLabel: string;
}) {
  return (
    <div className="flex items-center gap-4 border-b border-border/60 py-3.5 first:pt-0.5 last:border-b-0 last:pb-0.5">
      <span className="w-20 shrink-0 text-xs tabular-nums text-muted-foreground">{date}</span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{client}</p>
        <p className="truncate text-xs text-muted-foreground">{service}</p>
      </div>

      <span
        className="shrink-0 text-sm font-semibold tabular-nums text-foreground"
        style={{ fontFamily: "var(--font-heading)" }}
      >
        {formatCurrency(earned)}
      </span>

      <StatusPill tone={tone} label={statusLabel} className="hidden shrink-0 sm:inline-flex" />
    </div>
  );
}
